import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { MORE_SECTION_CONFIGS, QUICK_ACCESS_CATALOG, itemKeysForMoreSection } from '../../config/quickAccessItems'
import { openAssistantChat } from '../../utils/telegram'

type Accent = 'brand' | 'green' | 'orange' | 'red' | 'blue'

interface Tile {
  icon: string
  label: string
  path: string
  accent?: Accent
  tourId?: string
}

interface SectionProps {
  title: string
  tiles: Tile[]
}

const accentStyles: Record<Accent, { bg: string; color: string }> = {
  brand:  { bg: 'rgba(0,136,204,0.12)',  color: 'rgba(0,136,204,1)' },
  green:  { bg: 'rgba(34,197,94,0.12)',   color: 'rgba(22,163,74,1)' },
  orange: { bg: 'rgba(249,115,22,0.12)',  color: 'rgba(234,88,12,1)' },
  red:    { bg: 'rgba(239,68,68,0.12)',   color: 'rgba(220,38,38,1)' },
  blue:   { bg: 'rgba(0,136,204,0.12)',   color: 'rgba(0,136,204,1)' },
}

function TileGrid({ tiles, theme }: { tiles: Tile[]; theme: ReturnType<typeof useTheme> }) {
  const navigate = useNavigate()

  return (
    <div className="grid grid-cols-3 gap-3">
      {tiles.map(tile => {
        const accent = accentStyles[tile.accent ?? 'blue']
        return (
          <button
            key={tile.path}
            data-tour={tile.tourId}
            className="rounded-2xl p-3 text-left cursor-pointer active:scale-95 transition-transform flex flex-col gap-2"
            style={{
              backgroundColor: theme.cardBgLight,
              border: `1px solid ${theme.cardBorder}`,
            }}
            onClick={() => navigate(tile.path)}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
              style={{ background: accent.bg }}
            >
              {tile.icon}
            </div>
            <span
              className="text-xs font-semibold leading-tight"
              style={{ color: theme.text.primary }}
            >
              {tile.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function Section({ title, tiles, theme, tourId }: SectionProps & { theme: ReturnType<typeof useTheme>; tourId?: string }) {
  if (tiles.length === 0) return null

  return (
    <section className="mb-7" data-tour={tourId}>
      <h2
        className="text-xs font-bold uppercase tracking-widest mb-3"
        style={{ color: theme.text.muted }}
      >
        {title}
      </h2>
      <TileGrid tiles={tiles} theme={theme} />
    </section>
  )
}

const catalogMap = Object.fromEntries(QUICK_ACCESS_CATALOG.map(item => [item.key, item]))

export default function More() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const theme = useTheme()

  const isCoach = user?.hasTrainees ?? false
  const hasCoaches = user?.hasCoaches ?? false
  const hasActiveCoach = Boolean(user?.entitlements?.hasActiveCoach)
  const s = t.more.sections

  const sectionTitles: Record<string, string> = {
    clients:          s.clients.title,
    coachTraining:    s.coachTraining.title,
    personalTraining: s.personalTraining.title,
    account:          s.account.title,
  }

  function resolveTiles(itemKeys: string[]): Tile[] {
    return itemKeys.flatMap(key => {
      const item = catalogMap[key]
      if (!item) return []
      if (!item.roles.includes('any')) {
        if (isCoach && !item.roles.includes('coach')) return []
        if (!isCoach && !item.roles.includes('trainee')) return []
      }
      if (item.condition === 'hasCoaches' && !hasCoaches) return []
      return [{
        icon: item.emoji,
        label: t.quickAccess.items[item.labelKey],
        path: item.path,
        accent: item.accent,
        tourId:
          key === 'become-specialist' ? 'tour-more-become-coach'
          : key === 'training-blocks' ? 'tour-more-create-block'
          : key === 'training-programs' ? 'tour-more-create-program'
          : undefined,
      }]
    })
  }

  const sections = MORE_SECTION_CONFIGS
    .filter(sec => sec.roles.includes(isCoach ? 'coach' : 'trainee'))
    .filter(sec => !(sec.condition === 'hasCoaches' && !hasCoaches && !hasActiveCoach))
    .map(sec => ({
      key: sec.key,
      title: sectionTitles[sec.titleKey] ?? sec.titleKey,
      tiles: resolveTiles(itemKeysForMoreSection(sec, { isCoach, hasActiveCoach })),
      tourId:
        sec.key === 'coachTraining' ? 'tour-more-clients'
        : sec.key === 'traineeTraining' ? 'tour-more-with-coach'
        : sec.key === 'personalTraining' ? 'tour-more-personal'
        : sec.key === 'account' ? 'tour-more-account'
        : undefined,
    }))

  return (
    <div className="p-5">
      <header className="mb-6">
        <h1
          className="text-2xl font-extrabold"
          style={{ color: theme.text.primary, letterSpacing: '-0.4px' }}
        >
          {t.more.title}
        </h1>
      </header>

      {sections.map(sec => (
        <Section key={sec.key} title={sec.title} tiles={sec.tiles} theme={theme} tourId={sec.tourId} />
      ))}

      <button
        data-tour="tour-more-assistant"
        onClick={openAssistantChat}
        className="w-full text-left rounded-2xl p-5 mb-5 active:scale-[0.98] transition-transform"
        style={{
          backgroundColor: `${theme.buttonColor}12`,
          border: `1px solid ${theme.buttonColor}35`,
          boxShadow: theme.shadow.card,
        }}
      >
        <div className="text-3xl mb-3 leading-none">🤖</div>
        <p className="text-base font-bold leading-snug mb-1.5" style={{ color: theme.text.primary }}>
          {t.dashboard.assistantBanner.title}
        </p>
        <p className="text-xs leading-relaxed" style={{ color: theme.text.muted }}>
          {t.dashboard.assistantBanner.subtitle}
        </p>
        <div
          className="mt-4 py-2.5 rounded-xl text-center text-sm font-semibold"
          style={{ backgroundColor: theme.buttonColor, color: theme.buttonTextColor || '#fff' }}
        >
          {t.dashboard.assistantBanner.cta}
        </div>
      </button>
    </div>
  )
}
